import { en, type TranslationKey, type TranslationValue, type TranslationVariables } from "./locales/en.ts";
import { zhCN } from "./locales/zh-CN.ts";

export type AppLocale = "zh-CN" | "en";
export type LanguagePreference = "system" | AppLocale;
export type Translate = (
  key: TranslationKey,
  variables?: TranslationVariables,
) => string;

export const supportedLanguages: ReadonlyArray<{
  locale: AppLocale;
  nativeName: string;
  shortName: string;
}> = [
  { locale: "zh-CN", nativeName: "简体中文", shortName: "中" },
  { locale: "en", nativeName: "English", shortName: "EN" },
];

export const languageStorageKey = "orni-language";
const catalogs: Record<AppLocale, Record<TranslationKey, TranslationValue>> = {
  en,
  "zh-CN": zhCN,
};

export function detectSystemLocale(
  languages: readonly string[] = globalThis.navigator?.languages?.length
    ? globalThis.navigator.languages
    : [globalThis.navigator?.language ?? "en"],
): AppLocale {
  return languages.some((language) => language.toLowerCase().startsWith("zh"))
    ? "zh-CN"
    : "en";
}

export function readLanguagePreference(): LanguagePreference {
  try {
    const value = globalThis.localStorage?.getItem(languageStorageKey);
    if (value === "system" || value === "zh-CN" || value === "en")
      return value;
  } catch {
    // Restricted browser contexts can disable persistent storage.
  }
  return "system";
}

export function resolveLocale(preference: LanguagePreference): AppLocale {
  return preference === "system" ? detectSystemLocale() : preference;
}

export function applyLanguagePreference(
  preference: LanguagePreference,
): AppLocale {
  const locale = resolveLocale(preference);
  if (globalThis.document?.documentElement)
    globalThis.document.documentElement.lang = locale;
  try {
    globalThis.localStorage?.setItem(languageStorageKey, preference);
  } catch {
    // The language still applies for the current page lifetime.
  }
  return locale;
}

function interpolate(value: string, variables?: TranslationVariables): string {
  if (!variables) return value;
  return value.replace(/\{(\w+)\}/g, (match, key: string) =>
    variables[key] === undefined ? match : String(variables[key]),
  );
}

export function translateLocale(
  locale: AppLocale,
  key: TranslationKey,
  variables?: TranslationVariables,
): string {
  const message = catalogs[locale][key] ?? en[key];
  return typeof message === "function"
    ? message(variables ?? {})
    : interpolate(message, variables);
}

export function currentLocale(): AppLocale {
  return resolveLocale(readLanguagePreference());
}

export function translateCurrent(
  key: TranslationKey,
  variables?: TranslationVariables,
): string {
  return translateLocale(currentLocale(), key, variables);
}

export function formatLocaleDateTime(
  locale: AppLocale,
  value: string | number | Date,
): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function formatLocaleNumber(locale: AppLocale, value: number): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function localizeError(
  cause: unknown,
  t: Translate,
  fallbackKey: TranslationKey,
): string {
  const code =
    cause && typeof cause === "object" && "code" in cause
      ? String((cause as { code?: unknown }).code ?? "")
      : "";
  const keyByCode: Partial<Record<string, TranslationKey>> = {
    OFFLINE: "error.offline",
    UNAUTHENTICATED: "error.unauthenticated",
    NOT_FOUND: "error.notFound",
    CONFLICT: "error.conflict",
    PERMISSION_DENIED: "error.permissionDenied",
    INVALID_ARGUMENT: "error.invalidArgument",
    TIMEOUT: "error.timeout",
    UNAVAILABLE: "error.unavailable",
    UNSUPPORTED: "error.unsupported",
  };
  const key = keyByCode[code];
  if (key) return t(key);
  return cause instanceof Error && cause.message
    ? cause.message
    : t(fallbackKey);
}
